import { fetchOpenAIText } from "../utils/openaiClient";

export function useErrorCorrectionGeneratorLogic({
  errorCorrectionInput, setErrorCorrectionInput, errorCorrectionLevel,
  errorCorrectionSentenceCount, setErrorCorrectionSentenceCount,
  setErrorCorrectionLoading, generatedErrorCorrection, setGeneratedErrorCorrection,
  currentTab, onInsertToBoard, activeEditor,
  setShowErrorCorrectionGenerator, setSelectedTemplate,
}) {
  const handleErrorCorrectionGenerate = async () => {
    if (!errorCorrectionInput.trim()) return;

    setErrorCorrectionLoading(true);
    try {
      const levelSpecs = {
        A1: 'Very short, simple sentences (5-8 words); only present simple, "to be", basic plurals; one obvious error per sentence (e.g. verb "to be", plurals, basic word order).',
        A2: 'Short sentences (6-10 words); past simple, "going to" future, basic prepositions; errors in regular/irregular past tense, articles, simple prepositions.',
        B1: 'Sentences (8-14 words); past continuous, present perfect, first conditional, comparatives; errors in tense choice, subject-verb agreement, common phrasal verbs.',
        B2: 'Longer sentences (10-18 words); perfect tenses, passive voice, second conditional, reported speech; errors in more nuanced tense/aspect use, word order, prepositions.',
        C1: 'Complex sentences (12-20 words); third conditional, inversion, subtle collocations; errors that are subtle and require good command of English to spot.',
        C2: 'Sophisticated sentences (14-24 words); nuanced register, idiomatic usage, discourse markers; errors are subtle, near-native mistakes.',
      };

      const targetCount = errorCorrectionSentenceCount;

      // The model tends to anchor hard on whatever concrete example
      // sentences appear in the prompt, to the point of nearly copying them
      // verbatim regardless of the actual input — that's why generations
      // kept coming back with the same "I am going to the store yesterday" /
      // "She don't like coffee" pair. Forcing a random scenario per call
      // gives the model somewhere else to anchor to, so repeated generations
      // (even with identical input) land on different content.
      const VARIETY_THEMES = [
        'a family road trip', 'starting a new job', 'a cooking disaster', 'moving to a new apartment',
        'a school sports day', 'planning a birthday party', 'a trip to the doctor', 'losing a phone while traveling',
        'a job interview', 'adopting a pet', 'a weekend at the beach', 'a misunderstanding at a restaurant',
        'learning to drive', 'a surprise visit from relatives', 'shopping for a gift', 'a rainy camping trip',
        'starting university', 'a broken washing machine', 'a neighborhood festival', 'missing a flight',
      ];
      const theme = VARIETY_THEMES[Math.floor(Math.random() * VARIETY_THEMES.length)];

      const buildPrompt = (count) => `You are an expert English teacher creating an error correction exercise for ${errorCorrectionLevel} level students.

Generate exactly ${count} sentences inspired by this input (a topic, grammar point, or reference text): "${errorCorrectionInput}"

The input may be very brief — a single word, a short phrase, or just a grammar point name. That is completely fine: treat brevity as creative freedom, not a reason for a sparse or low-effort exercise. Use your own expert judgement to independently build a complete, coherent, ${errorCorrectionLevel}-appropriate exercise around it. Do not ask for more detail, do not comment on the input, and do not produce fewer than ${count} sentences no matter how short the input is.

Set the sentences in the context of "${theme}" — invent original characters, names, and details around this scenario rather than generic statements.

Level requirements (match strictly): ${levelSpecs[errorCorrectionLevel] || ''}

IMPORTANT GUIDELINES:
- Include diverse error types: verb tenses, articles (a/an/the), prepositions, word order, subject-verb agreement, countable/uncountable nouns, conditionals, passive voice, reported speech, and more
- Use VARIED SENTENCE TYPES: affirmative statements, negative statements, and questions (yes/no questions, wh-questions, tag questions)
- Make mistakes natural and authentic - the kind students actually make
- Ensure mistakes are challenging but appropriate for ${errorCorrectionLevel} level
- Provide 2-3 different correct answers when possible to show flexibility
- Include clear, educational explanations for each correction
- Focus on common ESL/EFL mistakes, not obscure grammar points
- Write completely original sentences every time. Never reuse stock textbook examples such as "I go to the store yesterday", "She don't like coffee", or similar — the example below shows ONLY the JSON structure, not content to copy or imitate

For each sentence, provide multiple possible correct answers. Return ONLY valid JSON in this exact format (the sentence content below is a structural placeholder only — replace it entirely with your own original content about "${theme}"):
{
  "title": "Error Correction Exercise",
  "level": "${errorCorrectionLevel}",
  "sentences": [
    {
      "id": "sentence_1",
      "text": "<original sentence with a grammar mistake, related to the theme>",
      "correctAnswers": [
        "<corrected version>",
        "<alternative acceptable correction, if applicable>"
      ],
      "explanation": "<brief explanation of the grammar rule>"
    }
  ]
}`;

      const runOnce = async (count) => {
        const raw = await fetchOpenAIText({ prompt: buildPrompt(count), temperature: 0.95, maxTokens: 2000 });
        if (!raw) return null;
        let contentStr = raw.trim();
        if (contentStr.startsWith('```json')) {
          contentStr = contentStr.replace(/^```json[\r\n]+/, '').replace(/```$/, '').trim();
        } else if (contentStr.startsWith('```')) {
          contentStr = contentStr.replace(/^```[\r\n]+/, '').replace(/```$/, '').trim();
        }
        try {
          return JSON.parse(contentStr);
        } catch (_) {
          return null;
        }
      };

      let parsed = await runOnce(targetCount);
      if (!parsed) parsed = await runOnce(targetCount);

      // Retry once for whatever's missing rather than padding with
      // duplicates or silently accepting an under-delivered set. Applies to
      // both a teacher-picked exact count and the custom-mode default, so a
      // minimal input can't quietly result in a 1-2 sentence exercise.
      if (parsed && Array.isArray(parsed.sentences) && parsed.sentences.length < targetCount) {
        const missing = targetCount - parsed.sentences.length;
        const fix = await runOnce(missing);
        if (fix && Array.isArray(fix.sentences)) {
          parsed = { ...parsed, sentences: [...parsed.sentences, ...fix.sentences.map((s, i) => ({ ...s, id: `sentence_${parsed.sentences.length + i + 1}` }))] };
        }
      }

      if (!parsed || !Array.isArray(parsed.sentences) || parsed.sentences.length === 0) {
        throw new Error('Could not generate the exercise — the AI response was invalid. Please try again.');
      }
      setGeneratedErrorCorrection(parsed);
    } catch (e) {
      alert(e?.message || 'Could not generate the exercise. Please try again.');
    } finally {
      setErrorCorrectionLoading(false);
    }
  };

  const handleErrorCorrectionInsert = () => {
    if (!generatedErrorCorrection) return;
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(generatedErrorCorrection, 'errorcorrection');
      setShowErrorCorrectionGenerator(false);
      setErrorCorrectionInput("");
      setGeneratedErrorCorrection(null);
      setSelectedTemplate(null);
      return;
    }
    if (!activeEditor) return;
    const errorCorrectionContent = {
      type: 'errorCorrection',
      attrs: {
        sentences: generatedErrorCorrection.sentences,
        title: generatedErrorCorrection.title,
        level: generatedErrorCorrection.level
      }
    };

    activeEditor.chain().focus().insertContent(errorCorrectionContent).run();
    setShowErrorCorrectionGenerator(false);
    setErrorCorrectionInput("");
    setGeneratedErrorCorrection(null);
    setSelectedTemplate(null);
  };

  const handleErrorCorrectionCancel = () => {
    setShowErrorCorrectionGenerator(false);
    setErrorCorrectionInput("");
    setErrorCorrectionSentenceCount(5);
    setGeneratedErrorCorrection(null);
    setSelectedTemplate(null);
  };

  return { handleErrorCorrectionGenerate, handleErrorCorrectionInsert, handleErrorCorrectionCancel };
}
